/**
 * Footer Component
 * Displays copyright information and links
 * @component Footer
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Email as EmailIcon, Phone as PhoneIcon, AccessTime as ClockIcon } from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import { translate, TranslationKey } from '../../../locales';
import { COMPANY_NAME, FOOTER_LINKS } from '../../../constants/footer';
import { UkraineFlag, USFlag } from '../../../components/Flags';
import ThemeToggle from '../../../components/ThemeToggle';
import './style.css';

interface SupportSettings {
  phone: string;
  email: string;
  working_hours: string;
}

const Footer: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const t = (key: TranslationKey) => translate(key, language as 'en' | 'ua');
  const currentYear = new Date().getFullYear();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [supportSettings, setSupportSettings] = useState<SupportSettings>({
    phone: '+38 (099) 109-02-89',
    email: 'support@onvisit.pro',
    working_hours: t('footer.workingHours')
  });

  const fetchSupportSettings = async () => {
    try {
      const response = await fetch('/api/v1/support/settings');
      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }
      const result = await response.json();
      setSupportSettings(result);
    } catch (error) {
      console.error('Error fetching support settings:', error);
      // Keep default values on error
    }
  };

  useEffect(() => {
    fetchSupportSettings();
  }, []);

  return (
    <footer className="app-footer">
      <div className="footer-content">
        <div className="footer-block">
          <span className="footer-copyright">
            © {COMPANY_NAME} {currentYear}. {t('footer.copyright')}
          </span>
        </div>
        <div className="footer-block footer-contact-block">
          <div className="footer-contact-link footer-working-hours">
            <ClockIcon color="primary" />
            <span className="footer-contact-text">{supportSettings.working_hours}</span>
          </div>
          <a href={`mailto:${supportSettings.email}`} className="footer-contact-link">
            <EmailIcon color="primary" />
            <span className="footer-contact-text">{supportSettings.email}</span>
          </a>
          <a href={`tel:${supportSettings.phone.replace(/\D/g, '').replace(/^38/, '')}`} className="footer-contact-link">
            <PhoneIcon color="primary" />
            <span className="footer-contact-text">{supportSettings.phone}</span>
          </a>
        </div>
        <div className="footer-block">
          <div className="footer-links">
            <span className="footer-separator">|</span>
            <Link to={FOOTER_LINKS.PRIVACY_POLICY} className="footer-link">
              {t('footer.privacyPolicy')}
            </Link>
            <span className="footer-separator">|</span>
            <Link to={FOOTER_LINKS.PUBLIC_OFFER} className="footer-link">
              {t('footer.termsOfUse')}
            </Link>
            <span className="footer-separator">|</span>
            <div className="footer-language-selector" style={{ position: 'relative' }}>
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="footer-language-button"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  color: 'inherit'
                }}
              >
                {language === 'ua' ? <UkraineFlag /> : <USFlag />}
              </button>
              {isLanguageDropdownOpen && (
                <div
                  className="footer-language-dropdown"
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '0',
                    background: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    minWidth: '60px'
                  }}
                >
                  <button
                    onClick={() => {
                      setLanguage('ua');
                      setIsLanguageDropdownOpen(false);
                    }}
                    className="footer-language-option"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px',
                      textAlign: 'left'
                    }}
                  >
                    <UkraineFlag />
                  </button>
                  <button
                    onClick={() => {
                      setLanguage('en');
                      setIsLanguageDropdownOpen(false);
                    }}
                    className="footer-language-option"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px',
                      textAlign: 'left'
                    }}
                  >
                    <USFlag />
                  </button>
                 
                </div>
              )}
            </div>
             <span className="footer-separator">|</span>
            <ThemeToggle />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
